/**
 * The status badge, now a glyph ON HOME ONLY (Round 5, Task 5 — Decision #7).
 * Her words: *"On the home screen, the in-progress and submitted badges take
 * up too much space. The screen is already divided into two sections: In
 * Progress and Submitted Surveys. The badges crowd the layout, and a long
 * building name could overlap, which would look bad. So an icon instead —
 * like a check mark for the ones submitted."*
 *
 * `.tag`/`.tag--{kind}` is shared with AdminScreen's roster and record rows,
 * which still print the status WORD in that same class pair — fix round 1
 * closed a regression where a global `.tag` rewrite clipped their text into a
 * 24px circle. `tag--glyph` is this component's own modifier, added alongside
 * `.tag`/`.tag--{kind}` rather than instead of them, so the badge stays the
 * class's shared meaning and only Home's reading of it changes shape.
 *
 * The WORD the text badge used to print does not leave the screen, only the
 * row: it moves into `aria-label`, and `role="img"` tells assistive tech to
 * read that label instead of trying to describe an SVG it cannot parse.
 * Three 24×24 hand-drawn shapes, one per `TagKind` — a filled circle with a
 * check for Submitted, a half-filled circle for In progress, an empty circle
 * for Assigned — deliberately drawn, not pulled from an icon library, to
 * match the hand-drawn glyphs already on this screen (`CameraGlyph`,
 * `MapTreeView.tsx`).
 *
 * Colour is never hard-coded here: every shape strokes and fills with
 * `currentColor`, so `.tag--glyph.tag--assigned` / `--field` / `--submitted`
 * (theme.css) set the ink. The check's own stroke is the one exception a
 * shape needs from its container rather than `currentColor` — it has to read
 * against the filled circle behind it, not blend into it — so it carries
 * `className="tag__check"` and theme.css paints it `var(--card)` instead.
 */

import type { ReactNode } from 'react';

import type { TagKind } from '../state/surveyList';

export interface StatusGlyphProps {
  kind: TagKind;
  label: string;
}

function SubmittedGlyph(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" stroke="currentColor">
      <circle cx="12" cy="12" r="10" fill="currentColor" stroke="none" />
      <path
        className="tag__check"
        d="M7.5 12.5l3 3 6-7"
        fill="none"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FieldGlyph(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" stroke="currentColor">
      <circle cx="12" cy="12" r="9.5" fill="none" strokeWidth="2" />
      <path d="M12 2.5a9.5 9.5 0 0 1 0 19z" fill="currentColor" stroke="none" />
    </svg>
  );
}

function AssignedGlyph(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" stroke="currentColor">
      <circle cx="12" cy="12" r="9.5" fill="none" strokeWidth="2" />
    </svg>
  );
}

const GLYPHS: Record<TagKind, () => ReactNode> = {
  submitted: SubmittedGlyph,
  field: FieldGlyph,
  assigned: AssignedGlyph,
};

export function StatusGlyph({ kind, label }: StatusGlyphProps): ReactNode {
  const Glyph = GLYPHS[kind];
  return (
    <span className={`tag tag--glyph tag--${kind}`} role="img" aria-label={label} data-legibility="display">
      <Glyph />
    </span>
  );
}
